import React from 'react';
import { Alert, Card } from 'react-bootstrap';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackType?: 'card' | 'block';
  title?: string;
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      const {
        fallbackType = 'card',
        title = 'Something went wrong!',
        showDetails = false,
      } = this.props;

      if (fallbackType === 'block') {
        return (
          <div className="error-boundary-block">
            <Alert variant="warning" className="mb-2">
              <Alert.Heading as="h6">⚠️ {title}</Alert.Heading>
              <p className="mb-2 text-muted small">
                <strong>ept AI backend error.</strong> Please send an email to{' '}
                <a href="mailto:support@ept.ai">support@ept.ai</a> with a
                screenshot or the text below.
              </p>
              <p className="mb-0">
                This content could not be displayed due to an error.
              </p>
              {showDetails && this.state.error && (
                <details className="mt-2">
                  <summary>Error Details</summary>
                  <pre className="error-details">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}
            </Alert>
          </div>
        );
      }

      // Default card fallback
      return (
        <Card className="mb-4 error-boundary-card">
          <Card.Body>
            <Alert variant="warning">
              <Alert.Heading>⚠️ {title}</Alert.Heading>
              <p className="mb-2 text-muted small">
                <strong>ept AI backend error.</strong> Please send an email to{' '}
                <a href="mailto:support@ept.ai">support@ept.ai</a> with a
                screenshot or the text below.
              </p>
              <p className="mb-0">
                This card could not be displayed due to an error in its content.
              </p>
              {showDetails && this.state.error && (
                <details className="mt-2">
                  <summary>Error Details</summary>
                  <pre className="error-details">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}
            </Alert>
          </Card.Body>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
